/**
 * Reading and editing column-width shares for a table.
 */

import { isJsonArray, readArray } from '@/domain/json'
import type { JsonValue } from '@/domain/json'

/**
 * Percentages of the table width, one per column.
 *
 * Anything that is not a finite number above zero is dropped; an empty list
 * means "let the layout decide". The renderer still checks the length against
 * the live column count before applying them.
 */
export function readColumnWidths(stored: JsonValue): number[] {
  const raw = readArray(stored, 'columnWidths')
  if (!isJsonArray(raw) || raw.length === 0) return []

  const widths: number[] = []
  for (const entry of raw) {
    if (typeof entry !== 'number' || !Number.isFinite(entry) || entry <= 0) continue
    widths.push(Math.round(entry * 10) / 10)
  }
  return widths
}

/**
 * Equal shares that sum to 100, for the "répartir" action in the inspector.
 */
export function equalColumnWidths(count: number): number[] {
  if (count <= 0) return []
  const share = Math.round((100 / count) * 10) / 10
  const widths = Array.from({ length: count }, () => share)
  const sum = widths.reduce((total, width) => total + width, 0)
  const last = widths.length - 1
  if (last >= 0) widths[last] = Math.round((widths[last]! + (100 - sum)) * 10) / 10
  return widths
}

/**
 * A label-heavy first column and equal shares for the rest.
 */
export function defaultImportedWidths(count: number, labelColumns = 1): number[] {
  if (count <= 0) return []
  if (count === 1) return [100]
  const labels = Math.min(Math.max(labelColumns, 1), count - 1)
  const labelShare = Math.min(36, Math.max(22, Math.round(48 / labels)))
  const used = labelShare * labels
  const restCount = count - labels
  const restShare = Math.round(((100 - used) / restCount) * 10) / 10
  const widths = [
    ...Array.from({ length: labels }, () => labelShare),
    ...Array.from({ length: restCount }, () => restShare),
  ]
  const sum = widths.reduce((total, width) => total + width, 0)
  const last = widths.length - 1
  if (last >= 0) widths[last] = Math.round((widths[last]! + (100 - sum)) * 10) / 10
  return widths
}

/** Whether a widths list can be applied to a table with this many columns. */
export function usableColumnWidths(
  widths: readonly number[] | undefined,
  columnCount: number,
): number[] | null {
  if (!widths || widths.length === 0 || widths.length !== columnCount) return null
  if (widths.some((width) => !(width > 0) || !Number.isFinite(width))) return null
  return [...widths]
}

/** Shares scaled so they add up to 100 again, to one decimal, the remainder on the last. */
function normalised(widths: readonly number[]): number[] {
  const total = widths.reduce((sum, width) => sum + width, 0)
  if (widths.length === 0 || !(total > 0)) return []
  const next = widths.map((width) => Math.round((width / total) * 100 * 10) / 10)
  const last = next.length - 1
  const sum = next.reduce((acc, width) => acc + width, 0)
  next[last] = Math.round((next[last]! + (100 - sum)) * 10) / 10
  return next
}

/**
 * The shares once a column is inserted before `at`.
 *
 * Only shares that matched the table are carried; anything else was already
 * being ignored and is left for the renderer to keep ignoring. The new column
 * takes an equal share and the others give it up in proportion, so a wide
 * label column stays the widest.
 */
export function widthsAfterInsert(
  widths: readonly number[],
  columns: number,
  at: number,
): number[] {
  if (widths.length !== columns) return [...widths]
  const next = [...widths]
  // A share of 100/n beside n shares adding to 100 is 1/(n+1) of the new total.
  next.splice(at, 0, 100 / columns)
  return normalised(next)
}

/** The shares once column `at` is removed, the rest rescaled to fill the table. */
export function widthsAfterRemove(
  widths: readonly number[],
  columns: number,
  at: number,
): number[] {
  if (widths.length !== columns) return [...widths]
  return normalised(widths.filter((_, position) => position !== at))
}

/** The shares once column `from` moves to `to`: the share travels with its column. */
export function widthsAfterMove(
  widths: readonly number[],
  columns: number,
  from: number,
  to: number,
): number[] {
  if (widths.length !== columns) return [...widths]
  const next = [...widths]
  const [moved] = next.splice(from, 1)
  if (moved !== undefined) next.splice(to, 0, moved)
  return next
}

/** About how many characters of body text a table set across the full measure holds on one line. */
const LINE_CHARS = 90
/** A column given less than this share of what its longest word needs breaks inside its words. */
const NARROW = 0.7

/**
 * The columns set too narrow for their own words: given less room than the
 * longest word in them takes, so a date or a heading breaks letter by letter.
 * Measured in characters against a line of the table's width, which is near
 * enough to tell 5 % for "31/12/25" from 18 %.
 */
export function narrowColumns(
  widths: readonly number[],
  texts: readonly (readonly string[])[],
): Set<number> {
  const narrow = new Set<number>()
  widths.forEach((share, column) => {
    const longest = Math.max(
      0,
      ...texts.map((row) =>
        Math.max(0, ...(row[column] ?? '').split(/\s+/).map((word) => word.length)),
      ),
    )
    const needed = (longest / LINE_CHARS) * 100
    if (share < needed * NARROW) narrow.add(column)
  })
  return narrow
}
